import { create } from '@/store'
import { devtools } from 'zustand/middleware'
import { navigate } from '@/hooks/use-navigation-listener'

const isSubpathOfOtherTabs = (parentPathname, subPathname, tabState) => {
  return Object.keys(tabState).some((key) => {
    if (key === parentPathname) return false

    const otherSubPathnames = tabState[key]?.subPathnames || []
    return otherSubPathnames.includes(subPathname)
  })
}

export const useTabNavStore = create(
  devtools(
    (set, get) => ({
      tabs: [],
      lastVisitedHref: '',
      tabState: {},
      addTab: (pathname, title, search = '') => {
        const currentTabs = get().tabs
        const existed = currentTabs.some((tab) => tab.pathname === pathname)
        if (existed) return

        const tab = { pathname, title, search }
        set((state) => ({ tabs: [...state.tabs, tab] }))

        if (currentTabs.length > 4) {
          const firstTab = currentTabs[0]
          get().removeTab(firstTab.pathname, true)
        }
      },
      updateTabTitle: (pathname, title) => {
        set((state) => {
          const newTab = state.tabs.map((e) => {
            if (e.pathname == pathname) {
              e.title = title
            }
            return e
          })
          return newTab
        })
      },
      removeTab: (pathname, noNavigation = false) =>
        set((state) => {
          const isActiveTab = window.location.pathname === pathname
          if (isActiveTab && !noNavigation) {
            const currentTabIndex = state.tabs.findIndex(
              (t) => t.pathname === pathname,
            )
            const nextTab =
              state.tabs[currentTabIndex + 1] || state.tabs[currentTabIndex - 1]
            if (nextTab) {
              navigate({
                to:
                  nextTab.pathname &&
                  nextTab.pathname !== window.location.pathname
                    ? nextTab.pathname
                    : '/tickets',
              })
            } else {
              navigate({
                to:
                  state.lastVisitedHref &&
                  state.lastVisitedHref !== window.location.pathname
                    ? state.lastVisitedHref
                    : '/tickets',
              })
            }
          }
          const pathnamesToRemove = []

          if (!isSubpathOfOtherTabs(pathname, pathname, state.tabState)) {
            pathnamesToRemove.push(pathname)
          }
          const tabState = state.tabState[pathname]
          const subPathnames = tabState?.subPathnames || []
          subPathnames.forEach((subPathname) => {
            const existingTab = state.tabs.find(
              (t) => t.pathname === subPathname,
            )
            if (existingTab) return

            if (isSubpathOfOtherTabs(pathname, subPathname, state.tabState))
              return

            pathnamesToRemove.push(subPathname)
          })
          return {
            tabs: state.tabs.filter((t) => t.pathname !== pathname),
            tabState: {
              ...state.tabState,
              ...Object.fromEntries(
                pathnamesToRemove.map((subPathname) => [
                  subPathname,
                  undefined,
                ]),
              ),
            },
          }
        }),
      setLastVisitedHref: (href) => set({ lastVisitedHref: href }),
      setTabState: (pathname, state) =>
        set((s) => ({ tabState: { ...s.tabState, [pathname]: state } })),
      setTicketForm: (pathname, form) =>
        set((state) => ({
          tabState: {
            ...state.tabState,
            [pathname]: { ...state.tabState[pathname], ticketForm: form },
          },
        })),
      setCustomerForm: (pathname, form) =>
        set((state) => ({
          tabState: {
            ...state.tabState,
            [pathname]: { ...state.tabState[pathname], customerForm: form },
          },
        })),

      setOrganizationForm: (pathname, form) =>
        set((state) => ({
          tabState: {
            ...state.tabState,
            [pathname]: { ...state.tabState[pathname], organizationForm: form },
          },
        })),
      setTicketCommentForm: (pathname, form) =>
        set((state) => ({
          tabState: {
            ...state.tabState,
            [pathname]: {
              ...state.tabState[pathname],
              ticketCommentForm: form,
            },
          },
        })),
      setTicketExtraFieldsForm: (pathname, form) =>
        set((state) => ({
          tabState: {
            ...state.tabState,
            [pathname]: {
              ...state.tabState[pathname],
              ticketExtraFieldsForm: form,
            },
          },
        })),

      setCustomerExtraFieldsForm: (pathname, form) =>
        set((state) => ({
          tabState: {
            ...state.tabState,
            [pathname]: {
              ...state.tabState[pathname],
              customerExtraFieldsForm: form,
            },
          },
        })),

      resetTabs: () => set({ tabs: [] }),
    }),
    {
      name: 'tab-nav-store',
    },
  ),
)
